import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const initialFormData = {
  name: "",
  email: "",
  password: ""
};

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setLoadError("");
      const res = await axios.get(API_BASE_URL + "/api/accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setLoadError("Accounts list is not available yet");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingAccountId(null);
    setShowPassword(false);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (account) => {
    setFormData({
      name: account.name || "",
      email: account.email || "",
      password: ""
    });
    setEditingAccountId(account._id);
    setShowPassword(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleChange = (event) => {
    setFormData((current) => ({
      ...current,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      if (editingAccountId) {
        const payload = { ...formData };
        if (!payload.password) {
          delete payload.password;
        }

        const res = await axios.put(
          API_BASE_URL + `/api/accounts/${editingAccountId}`,
          payload
        );

        setAccounts((current) =>
          current.map((account) => (account._id === editingAccountId ? res.data : account))
        );
        alert("Accounts user updated successfully");
      } else {
        const res = await axios.post(API_BASE_URL + "/api/accounts", formData);
        setAccounts((current) => [res.data, ...current]);
        alert("Accounts user created successfully");
      }

      closeForm();
    } catch (error) {
      console.error("Error saving accounts user:", error);
      alert(error.response?.data?.message || "Error saving accounts user");
    } finally {
      setSaving(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return accounts;

    return accounts.filter((account) =>
      [account.name, account.email, account._id]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [accounts, search]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 lg:mb-6 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Accounts Directory</h1>
        <div className="flex w-full gap-2 lg:w-auto">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search accountant, email..."
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 lg:w-80"
          />
          <button type="button" onClick={() => setSearch((current) => current.trim())} className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white lg:hidden">
            Search
          </button>
        </div>
        <button
          onClick={openCreateForm}
          className="hidden rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white shadow transition hover:bg-blue-700 lg:block lg:py-2"
        >
          + Add New Accountant
        </button>
      </div>
      <button
        onClick={openCreateForm}
        className="mb-4 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow transition hover:bg-blue-700 lg:hidden"
      >
        + Add New Accountant
      </button>

      {loadError && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Accounts list is not available from the backend yet. You can still open the form; saving needs the local backend running with the latest code.
        </div>
      )}

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-lg sm:p-6">
          <div className="mb-4 flex flex-col gap-3 border-b pb-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold text-blue-900">
              {editingAccountId ? "Edit Accountant" : "Register Accountant"}
            </h2>

            <button
              type="button"
              onClick={closeForm}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-bold text-gray-700">Name</label>
              <input
                type="text"
                name="name"
                placeholder="Full name"
                value={formData.name}
                onChange={handleChange}
                className="rounded border p-2 outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="mb-1 text-sm font-bold text-gray-700">Email ID</label>
              <input
                type="email"
                name="email"
                placeholder="accountant@company.com"
                value={formData.email}
                onChange={handleChange}
                className="rounded border p-2 outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="mb-1 text-sm font-bold text-gray-700">
                {editingAccountId ? "New Password" : "Password"}
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={
                  editingAccountId
                    ? "Leave blank to keep current password"
                    : "Create login password"
                }
                value={formData.password}
                onChange={handleChange}
                className="rounded border p-2 outline-none focus:ring-2 focus:ring-blue-400"
                required={!editingAccountId}
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                className="mt-2 text-left text-sm text-blue-600 hover:underline"
              >
                {showPassword ? "Hide Password" : "View Password"}
              </button>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="rounded bg-green-600 px-4 py-3 font-bold text-white shadow-md transition hover:bg-green-700 disabled:bg-gray-400 md:col-span-2"
            >
              {saving
                ? "Saving..."
                : editingAccountId
                  ? "Update Accountant Details"
                  : "Save Accountant Details"}
            </button>
          </form>
        </div>
      )}

      <div className="space-y-4 lg:hidden">
        {loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
            Loading accounts...
          </div>
        )}

        {!loading && filteredAccounts.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
            No accountants added yet
          </div>
        )}

        {!loading && filteredAccounts.map((account) => (
          <article key={account._id} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">Accountant</p>
            <div className="mt-2 flex items-start justify-between gap-4">
              <h2 className="min-w-0 text-lg font-bold leading-snug text-gray-900">{account.name || "-"}</h2>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500 font-bold text-stone-950">
                {(account.name || "A").charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">Email ID</p>
              <p className="mt-1 break-words text-sm text-gray-700">{account.email || "-"}</p>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => openEditForm(account)}
                className="rounded-xl bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-yellow-600"
              >
                Edit
              </button>
            </div>
          </article>
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md lg:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead className="border-b bg-gray-100 text-gray-700">
              <tr>
                <th className="p-4 text-left font-bold">Name</th>
                <th className="p-4 text-left font-bold">Email ID</th>
                <th className="p-4 text-left font-bold">Password</th>
                <th className="p-4 text-center font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-gray-500">
                    Loading accounts...
                  </td>
                </tr>
              )}

              {!loading && filteredAccounts.length === 0 && (
                <tr>
                  <td colSpan="4" className="p-6 text-center text-gray-500">
                    No accountants added yet
                  </td>
                </tr>
              )}

              {!loading && filteredAccounts.map((account) => (
                <tr key={account._id} className="transition hover:bg-gray-50">
                  <td className="p-4">
                    <div className="font-bold text-gray-900">{account.name}</div>
                    <div className="text-xs font-mono text-blue-600">{account._id}</div>
                  </td>
                  <td className="p-4 text-sm text-gray-600">{account.email || "-"}</td>
                  <td className="p-4 text-sm text-gray-600">Saved securely</td>
                  <td className="p-4 text-center">
                    <button
                      type="button"
                      onClick={() => openEditForm(account)}
                      className="rounded bg-yellow-500 px-3 py-1 text-sm text-white hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
